import { useCallback, useEffect, useState } from 'react';

import { FindAStore } from '../FindAStore';
import { LoginButton } from '../LoginButton';
import { setProfile } from '../../../Account/state/profileSlice';
import { SocialLinks } from '../../../components/SocialLinks';

import { useAppDispatch, useAppSelector } from '../../../reduxstore/hooks';
import { deleteAccessToken, getUserSession } from '../../../utils/userStorage';
import { useOAuthRefreshMutation } from '../../../Auth/api/oauth';
import {
	clearAuthState,
	setBirthdaySet,
	setOAuthTokens,
	setUserId
} from '../../../domains/Auth/slice';
import { useLazyGetProfileInfoQuery } from '../../../Account/api';

import {
	deleteLocalInfo,
	getLocalAccessToken,
	getLocalRefreshToken,
	getLocalUserId,
	setLocalAccessToken,
	setLocalRefreshToken,
	setLocalUserId
} from '../../../MedianFunctions';
import { setUserSession } from '../../../utils/userStorage';

import { Button } from '../UserMenu/Button';
import { useNavigate } from 'react-router-dom';
import {
	showTermsPrompt,
	hideTermsPrompt
} from '../../../domains/UI/store/slice';

export const TopBar: React.FC = () => {
	const dispatch = useAppDispatch();
	// const authSelector = useAppSelector(state => state.authReducer);
	const [authenticated, setAuthenticated] = useState<boolean>(false);
	const [getNewAccessToken] = useOAuthRefreshMutation();
	const [getProfileInfo] = useLazyGetProfileInfoQuery();

	const navigate = useNavigate();

	const session = useAppSelector(state => state.authReducer);
	const profileId = useAppSelector(state => state.accountReducer.profile.id);
	const medianIsLoaded = navigator.userAgent.indexOf('median') > -1;

	const restoreProfileData = useCallback(
		async (session: AuthState) => {
			if (!session.accessToken && !session.refreshToken) {
				return;
			}

			if (session.accessToken) {
				const res = await getProfileInfo(null);

				if (res.data) {
					const userInfo = res.data.data;
					dispatch(setUserId(userInfo.id));
					dispatch(setProfile(userInfo.profile as ProfileState));
					dispatch(setBirthdaySet(userInfo.birthday_set));
					if (userInfo.acknowledged_terms) {
						dispatch(hideTermsPrompt());
					} else {
						dispatch(showTermsPrompt());
					}

					// Median
					if (navigator.userAgent.indexOf('median') > -1) {
						setLocalUserId(String(userInfo.id));
					}

					const userSession = {
						...session,
						id: userInfo.id
					} as AuthState;
					setUserSession(userSession);

					return;
				}
			}

			// dont handle error, allow next request of trying refresh token

			if (session.refreshToken) {
				const OAuthRefreshParams = {
					client_id: import.meta.env.VITE_APP_OAUTH_CLIENT_ID,
					refresh_token: session.refreshToken,
					grant_type: 'refresh_token'
				} as OAuthRefreshPayload;

				const encodedPayload = new URLSearchParams({
					...OAuthRefreshParams
				});

				try {
					const newTokens = await getNewAccessToken(encodedPayload);
					const data = (newTokens as any).data;

					if ((newTokens as any).error) {
						// const error = (newTokens as any).error;

						if (medianIsLoaded) {
							deleteLocalInfo();
						}
						dispatch(clearAuthState());
						deleteAccessToken();

						navigate('/menu');

						return;
					}

					if ((newTokens as any).data) {
						const userSessionData = {
							id: session?.id,
							accessToken: data.access_token,
							refreshToken: data.refresh_token
						} as AuthState;

						//localStorage
						setUserSession(userSessionData);
						//redux
						dispatch(
							setOAuthTokens({
								accessToken: userSessionData.accessToken,
								refreshToken: userSessionData.refreshToken
							} as OAuthPayload)
						);
						dispatch(setUserId(userSessionData.id as number));

						// Median
						if (medianIsLoaded) {
							setLocalAccessToken(
								userSessionData.accessToken as string
							);
							setLocalRefreshToken(
								userSessionData.refreshToken as string
							);
							setLocalUserId(String(userSessionData.id));
						}
					}
				} catch {
					if (medianIsLoaded) {
						deleteLocalInfo();
					}
					dispatch(clearAuthState());
					deleteAccessToken();
					setAuthenticated(false);

					navigate('/menu');
				}
			}
		},
		[dispatch, getNewAccessToken, getProfileInfo, medianIsLoaded, navigate]
	);

	const checkLocalTokens = useCallback(async (): Promise<boolean> => {
		try {
			const accessToken = await getLocalAccessToken();
			const refreshToken = await getLocalRefreshToken();
			const userId: number = await getLocalUserId();

			if (accessToken && refreshToken) {
				dispatch(
					setOAuthTokens({
						accessToken,
						refreshToken
					})
				);
			}

			if (userId) {
				dispatch(setUserId(+userId));
			}
			setAuthenticated(true);
			return true;
		} catch (err) {
			setAuthenticated(false);
			return false;
		}
	}, [dispatch]);

	const checkLocalStorage = useCallback((): boolean => {
		const userSession = getUserSession();

		if (userSession) {
			const accessToken = userSession.accessToken;
			const refreshToken = userSession.refreshToken;
			const userId = userSession.id;

			if (!accessToken || !refreshToken) {
				return false;
			}

			dispatch(
				setOAuthTokens({
					accessToken,
					refreshToken
				})
			);
			if (userId) {
				dispatch(setUserId(userId));
			}
			setAuthenticated(true);
			return true;
		} else {
			setAuthenticated(false);
			// navigate('/menu');
			return false;
		}
	}, [dispatch]);

	useEffect(() => {
		/// check authenticated
		if (!authenticated) {
			if (medianIsLoaded) {
				checkLocalTokens();
			} else {
				checkLocalStorage();
			}
			return;
		}

		if (profileId) {
			return;
		}

		restoreProfileData(session);
	}, [
		profileId,
		authenticated,
		checkLocalStorage,
		checkLocalTokens,
		medianIsLoaded,
		session,
		restoreProfileData
	]);

	return (
		<div className="d-none d-lg-flex header-top-bar w-100">
			<div className="py-1 bg-light-primary flex-grow-1" />

			<div className="container bg-primary">
				<div className="row no-gutters">
					<div className="col-3 bg-light-primary">
						<FindAStore />
					</div>

					<div className="col-auto">
						{session.accessToken ? <Button /> : <LoginButton />}
					</div>

					<div className="col d-flex pl-3">
						<div className="ml-auto">
							<SocialLinks withLabel />
						</div>
					</div>
				</div>
			</div>

			<div className="py-1 bg-primary flex-grow-1" />
		</div>
	);
};

interface OAuthRefreshPayload {
	[key: string]: string;
}
